import { useEffect, useMemo, useRef, useState } from 'react';
import { createFileRoute, useRouter } from '@tanstack/react-router';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
  Avatar,
  DataTable,
  type DataTableColumn,
  EmptyState,
  FilterMultiSelect,
  FilterSelect,
  PageBody,
} from '@wundo/ui';
import { PageHeader } from '@/components/PageHeader.tsx';
import { DateRangeFilter } from '@/components/DateRangeFilter.tsx';
import { DEFAULT_DATE_RANGE_OPTIONS } from '@/components/dateRangeOptions.ts';
import { SeverityFilterChips } from '@/components/conversationReview/SeverityFilterChips.tsx';
import { SEVERITY_ORDER } from '@/components/conversationReview/severity.ts';
import {
  FINDING_CLASS_GROUP_STARTS,
  FINDING_CLASS_LABELS,
  FINDING_CLASS_ORDER,
} from '@/components/conversationReview/findingClasses.ts';
import { requireInternalOrganisation } from '@/lib/staffGate.ts';
import { useOrganisationFilter } from '@/lib/activeOrganisation.ts';
import { useResetOnChange } from '@/lib/useResetOnChange.ts';
import {
  type ConversationReview,
  fetchReviewCounts,
  fetchReviews,
  type FindingClass,
  type ReviewFilters,
  type ReviewHandledFilter,
  type ReviewSeverity,
} from '@/lib/conversationReviews.ts';
import { PAGE_SIZE } from '@/lib/pageSize.ts';
import { Check } from 'lucide-react';
import { formatRelativeTime } from '@/utils/dateUtils.ts';
import { FindingPills } from '@/components/conversationReview/FindingPills.tsx';
import { SeverityPill } from '@/components/conversationReview/SeverityPill.tsx';
import { UserMessagesLink } from '@/components/conversationReview/UserMessagesLink.tsx';

/** The filters, as they sit in the URL. The organisations follow the header picker instead. */
export interface ReviewsSearch {
  severity?: string;
  class?: string;
  range?: string;
  handled?: ReviewHandledFilter;
}

const STORAGE_KEY = 'conversationReviews.filters';

const HANDLED_OPTIONS: { value: ReviewHandledFilter; label: string }[] = [
  { value: 'open', label: 'Open findings' },
  { value: 'handled', label: 'Handled findings' },
  { value: 'all', label: 'All findings' },
];

function asQuery(value: unknown): string | undefined {
  return typeof value === 'string' && value !== '' ? value : undefined;
}

/* A hand-edited URL keeps only the values it knows, so an unknown one cannot fail the request. */
function asKnownList(value: unknown, known: readonly string[]): string | undefined {
  const kept =
    asQuery(value)
      ?.split(',')
      .filter((item) => known.includes(item)) ?? [];
  return kept.length > 0 ? kept.join(',') : undefined;
}

/* "open" is the default, and a default does not belong in a URL. */
function asHandled(value: unknown): ReviewHandledFilter | undefined {
  return value === 'handled' || value === 'all' ? value : undefined;
}

export const Route = createFileRoute('/_app/conversation-reviews/')({
  staticData: { orgScope: 'optional' },
  validateSearch: (search: Record<string, unknown>): ReviewsSearch => ({
    severity: asKnownList(search.severity, SEVERITY_ORDER),
    class: asKnownList(search.class, FINDING_CLASS_ORDER),
    /* "all" is the default, and a default does not belong in a URL. */
    range: asQuery(search.range) === 'all' ? undefined : asQuery(search.range),
    handled: asHandled(search.handled),
  }),
  beforeLoad: ({ context }) => requireInternalOrganisation(context.queryClient),
  component: ConversationReviewsRoute,
});

interface PersistedFilters {
  severity?: string[];
  class?: string[];
  range?: string;
  handled?: string;
}

function readPersisted(): PersistedFilters | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as PersistedFilters) : null;
  } catch {
    /* localStorage may be unavailable (Safari private mode) */
    return null;
  }
}

function writePersisted(search: ReviewsSearch): void {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        severity: search.severity ? search.severity.split(',') : [],
        class: search.class ? search.class.split(',') : [],
        range: search.range ?? 'all',
        handled: search.handled ?? 'open',
      }),
    );
  } catch {
    /* localStorage may be unavailable (Safari private mode) */
  }
}

function ConversationReviewsRoute() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const restored = useRef(false);

  /*
   * A visit with no filters in the URL picks up where the last one left off.
   * Once, on the first render: after that an empty URL means "cleared", and
   * restoring then would make the chips impossible to switch off.
   */
  useEffect(() => {
    if (restored.current) return;
    restored.current = true;

    if (search.severity || search.class || search.range || search.handled) return;

    const persisted = readPersisted();
    if (!persisted) return;

    const next: ReviewsSearch = {
      severity: persisted.severity?.length ? persisted.severity.join(',') : undefined,
      class: persisted.class?.length ? persisted.class.join(',') : undefined,
      range: persisted.range && persisted.range !== 'all' ? persisted.range : undefined,
      handled: asHandled(persisted.handled),
    };

    if (next.severity || next.class || next.range || next.handled) {
      void navigate({ search: next, replace: true });
    }
  }, [navigate, search]);

  return (
    <ConversationReviewsPage
      search={search}
      onSearchChange={(next) => {
        writePersisted(next);
        void navigate({ search: next });
      }}
    />
  );
}

/* Counted back from this minute, not from midnight. */
function rangeStart(range: string): string | undefined {
  const days = DEFAULT_DATE_RANGE_OPTIONS.find((option) => option.value === range)?.days;
  if (!days) return undefined;

  const from = new Date();
  from.setDate(from.getDate() - days);
  return from.toISOString();
}

function classButtonLabel(classes: FindingClass[]): string {
  if (classes.length === 0) return 'All classes';
  if (classes.length === 1) return FINDING_CLASS_LABELS[classes[0]];
  return `${classes.length} classes`;
}

export interface ConversationReviewsPageProps {
  search: ReviewsSearch;
  onSearchChange: (search: ReviewsSearch) => void;
}

/**
 * Every conversation the automated reviewer has looked at, worst first.
 *
 * The filters live in the URL, so a link carries the list it came from. The
 * list opens on the findings nobody has handled yet.
 */
export function ConversationReviewsPage({
  search,
  onSearchChange,
}: ConversationReviewsPageProps) {
  const router = useRouter();
  /* Another organisation in the header is another list, read from its first page. */
  const headerOrganisationId = useOrganisationFilter()?.id ?? '';
  const [page, setPage] = useState(1);
  useResetOnChange(headerOrganisationId, () => setPage(1));

  const range = search.range ?? 'all';
  const handled = search.handled ?? 'open';
  const severities = useMemo(
    () => (search.severity ? (search.severity.split(',') as ReviewSeverity[]) : []),
    [search.severity],
  );
  const classes = useMemo(
    () => (search.class ? (search.class.split(',') as FindingClass[]) : []),
    [search.class],
  );

  const filters = useMemo<ReviewFilters>(
    () => ({
      severity: severities,
      class: classes,
      handled,
      since: rangeStart(range),
      organisation_ids: headerOrganisationId === '' ? [] : [headerOrganisationId],
    }),
    [severities, classes, handled, range, headerOrganisationId],
  );

  const reviews = useQuery({
    queryKey: ['conversationReviews', filters, page],
    queryFn: () => fetchReviews(filters, page),
    placeholderData: keepPreviousData,
    throwOnError: true,
  });

  const counts = useQuery({
    queryKey: [
      'conversationReviewCounts',
      filters.class,
      filters.handled,
      filters.since,
      filters.organisation_ids,
    ],
    queryFn: () => fetchReviewCounts(filters),
  });

  const rows = reviews.data?.data ?? [];
  const total = reviews.data?.meta.totalItems ?? 0;

  /* Any filter change starts the list again from its first page. */
  function change(next: ReviewsSearch) {
    setPage(1);
    onSearchChange(next);
  }

  function toggleSeverity(severity: ReviewSeverity) {
    const next = severities.includes(severity)
      ? severities.filter((value) => value !== severity)
      : [...severities, severity];
    change({ ...search, severity: next.length ? next.join(',') : undefined });
  }

  function toggleClass(findingClass: FindingClass) {
    const next = classes.includes(findingClass)
      ? classes.filter((value) => value !== findingClass)
      : [...classes, findingClass];
    change({ ...search, class: next.length ? next.join(',') : undefined });
  }

  return (
    <>
      <PageHeader
        title='Conversation reviews'
        subtitle='What the automated reviewer found wrong in customer conversations'
      />
      <PageBody className='mt-3.5'>
        <DataTable<ConversationReview>
          label='Conversation reviews'
          columns={reviewColumns}
          rows={rows}
          rowKey={(review) => review.id}
          loading={reviews.isFetching}
          pagination={{ page, pageSize: PAGE_SIZE, total }}
          onPageChange={setPage}
          rowHref={(review) => `/conversation-reviews/${review.id}`}
          onNavigate={(href) => void router.navigate({ href })}
          emptyState={
            handled === 'open' ? (
              <EmptyState
                title='No open findings'
                description='Refusals and misuse attempts are recorded, not failures: find them under All findings.'
              />
            ) : (
              <EmptyState title='No reviews found' />
            )
          }
          toolbar={
            <>
              <SeverityFilterChips
                selected={severities}
                counts={counts.data ?? null}
                onToggle={toggleSeverity}
              />
              <FilterMultiSelect<FindingClass>
                hideLabel
                label='Class'
                buttonLabel={classButtonLabel(classes)}
                options={FINDING_CLASS_ORDER.map((findingClass) => ({
                  value: findingClass,
                  label: FINDING_CLASS_LABELS[findingClass],
                  checked: classes.includes(findingClass),
                  separatorBefore: FINDING_CLASS_GROUP_STARTS.has(findingClass),
                }))}
                onSelect={toggleClass}
              />
              <FilterSelect<ReviewHandledFilter>
                hideLabel
                label='Status'
                options={HANDLED_OPTIONS}
                value={handled}
                onValueChange={(next) =>
                  change({ ...search, handled: next === 'open' ? undefined : next })
                }
              />
              <DateRangeFilter
                value={range}
                onChange={(next) =>
                  change({ ...search, range: next === 'all' ? undefined : next })
                }
              />
            </>
          }
        />
      </PageBody>
    </>
  );
}

const reviewColumns: DataTableColumn<ConversationReview>[] = [
  {
    key: 'severity',
    header: 'Severity',
    render: (review) => <SeverityPill severity={review.severity} />,
  },
  {
    key: 'findings',
    header: 'Findings',
    width: 'w-80 max-mobile:w-auto',
    render: (review) => <FindingPills findings={review.findings} />,
  },
  {
    key: 'user_name',
    header: 'User',
    hideBelow: 'mobile',
    render: (review) => (
      <span className='flex flex-col gap-0.5'>
        <UserMessagesLink
          userId={review.user_id}
          className='inline-flex items-center gap-2 whitespace-nowrap text-ink hover:underline'
        >
          <Avatar
            size='sm'
            name={review.user_name?.trim() || '?'}
            className='size-6 text-2xs'
          />
          {review.user_name?.trim() || '—'}
        </UserMessagesLink>
        {review.organisation_name && (
          <span className='pl-8 text-xs text-ink-muted'>{review.organisation_name}</span>
        )}
      </span>
    ),
  },
  {
    key: 'session_title',
    header: 'Session',
    hideBelow: 'mobile',
    render: (review) =>
      review.session_title ? (
        <span className='line-clamp-2 text-ink'>{review.session_title}</span>
      ) : (
        <span className='text-ink-muted'>Untitled session</span>
      ),
  },
  {
    key: 'created_at',
    header: 'Time',
    render: (review) => (
      <span
        className='whitespace-nowrap tabular-nums text-ink-muted'
        title={new Date(review.created_at).toLocaleString()}
      >
        {formatRelativeTime(review.created_at)}
      </span>
    ),
  },
  {
    key: 'handled_at',
    header: 'Handled',
    hideBelow: 'mobile',
    render: (review) =>
      review.handled_at ? (
        <span
          className='inline-flex items-center gap-1.5 whitespace-nowrap'
          title={
            review.handled_by_name
              ? `Handled by ${review.handled_by_name}`
              : new Date(review.handled_at).toLocaleString()
          }
        >
          <Check className='size-3.5 text-success' aria-label='Handled' />
          <span className='tabular-nums text-ink-muted'>
            {formatRelativeTime(review.handled_at)}
          </span>
        </span>
      ) : null,
  },
];
